/**
 * Storefront buyer-confidence ticker (TNK-208). A strip above the hero on
 * /shop/inventory that surfaces operator-enabled items (cards shipped, in
 * stock, prices from, just sold, recently added, games, custom lines). Motion
 * is operator-chosen: fade (one item every ~4s), marquee (continuous
 * right-to-left scroll with 25% edge fades), or static (joined on one line).
 * Reduced-motion or a single item collapses to static. Renders nothing when
 * there are no items.
 *
 * Marquee seamlessness: we measure one copy ("unit") + the visible width,
 * render enough copies to always overflow, and translate by exactly one unit
 * in pixels — so copy N+1 lands exactly on copy N and the loop reset is
 * invisible (no snap). Duration is derived from the unit width for a constant
 * pixel speed regardless of how many items are enabled.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion as fm } from "framer-motion";

type TickerMotion = "fade" | "marquee" | "static";
type TickerSpeed = "slow" | "medium" | "fast";

// Marquee scroll speed in px/sec.
const PX_PER_SECOND: Record<TickerSpeed, number> = {
  slow: 28,
  medium: 50,
  fast: 85,
};

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const onChange = () => setReduce(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduce;
}

export function Ticker({
  items,
  motion = "fade",
  speed = "medium",
  gap = 48,
  flush = true,
}: {
  items: string[];
  motion?: TickerMotion;
  speed?: TickerSpeed;
  /** Space between items in px (also the trailing gap before the next copy). */
  gap?: number;
  /** Full-bleed flush strip (page use, default) vs a contained box (preview). */
  flush?: boolean;
}) {
  const reduce = usePrefersReducedMotion();
  const [idx, setIdx] = useState(0);
  const effective: TickerMotion =
    reduce || items.length <= 1 ? "static" : motion;

  // Fade rotation: cycle the visible item.
  useEffect(() => {
    setIdx(0);
  }, [items.length]);
  useEffect(() => {
    if (effective !== "fade" || items.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 4000);
    return () => clearInterval(t);
  }, [effective, items.length]);

  // Marquee measurement (see file header). itemsKey keeps the effect from
  // re-running on every parent render (items is a fresh array each time).
  const itemsKey = items.join("␟");
  const containerRef = useRef<HTMLDivElement>(null);
  const unitRef = useRef<HTMLDivElement>(null);
  const [unitWidth, setUnitWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  useLayoutEffect(() => {
    if (effective !== "marquee") return;
    const measure = () => {
      if (unitRef.current) setUnitWidth(unitRef.current.offsetWidth);
      if (containerRef.current)
        setContainerWidth(containerRef.current.offsetWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    if (unitRef.current) ro.observe(unitRef.current);
    return () => ro.disconnect();
  }, [effective, itemsKey, gap]);

  // Enough copies that the track always overflows the viewport (+1 spare), so
  // shifting by one unit never reveals empty space before the reset.
  const copies =
    unitWidth > 0 ? Math.max(2, Math.ceil(containerWidth / unitWidth) + 1) : 2;
  const duration = unitWidth > 0 ? unitWidth / PX_PER_SECOND[speed] : 20;

  if (items.length === 0) return null;

  return (
    <div
      className={`overflow-hidden px-4 py-1.5 text-[11px] font-semibold ${
        flush
          ? "-mx-4 -mt-6 border-b border-border sm:-mx-6"
          : "rounded-md border border-border"
      }`}
      style={{ background: "rgba(74,222,128,0.10)", color: "#4ade80" }}
      aria-live="off"
    >
      {effective === "static" && (
        <div
          className="flex flex-wrap items-center justify-center"
          style={{ gap: `${gap}px` }}
        >
          {items.map((it, i) => (
            <span key={i}>{it}</span>
          ))}
        </div>
      )}

      {effective === "fade" && (
        <div className="relative flex h-4 items-center justify-center">
          <AnimatePresence mode="wait">
            <fm.span
              key={idx}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.4 }}
              className="absolute whitespace-nowrap"
            >
              {items[idx % items.length]}
            </fm.span>
          </AnimatePresence>
        </div>
      )}

      {effective === "marquee" && (
        // shop-ticker-fade masks the edges: items fade in over the right 25%
        // (entry) and fade out over the left 25% (exit), opaque through the
        // middle. The track scrolls left by exactly one unit (--ticker-shift).
        <div ref={containerRef} className="shop-ticker-fade">
          <div
            className="flex w-max shop-ticker-marquee"
            style={
              {
                animationDuration: `${duration}s`,
                "--ticker-shift": `${unitWidth}px`,
              } as React.CSSProperties
            }
          >
            {Array.from({ length: copies }).map((_, c) => (
              <div
                key={c}
                ref={c === 0 ? unitRef : undefined}
                aria-hidden={c > 0}
                className="flex shrink-0 items-center"
                style={{ gap: `${gap}px`, paddingRight: `${gap}px` }}
              >
                {items.map((it, i) => (
                  <span key={i} className="whitespace-nowrap">
                    {it}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
